/**
 * 店舗タブの表示用の変換（純粋関数）。GET /dashboard/store-summary と /admin/store-buckets の応答を、
 * 画面（ヒーロー・人数ブロック・スコアカード・全店比較）が読める形にする。
 *
 * 判定（色・区分）はサーバーの status をそのまま使い、ここでは閾値を持たない。数字の整形だけ。
 * 先月同時期との比較は、今月累計（mtd）と先月同時期（prev_month_same_period）を使う
 * （どちらも日次ビューから取った同じキーなので、同じ基準で比べられる）。
 */

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
const round1 = (v) => Math.round(v * 10) / 10;

/** 増減率（%・小数 1 桁）。比べる先が無い・0 のときは null。 */
export function pctChange(cur, prev) {
  const c = num(cur);
  const p = num(prev);
  if (c == null || p == null || p === 0) return null;
  return round1(((c - p) / p) * 100);
}

/** 率の差（ポイント・小数 1 桁）。どちらかが無ければ null。 */
export function pointChange(cur, prev) {
  const c = num(cur);
  const p = num(prev);
  if (c == null || p == null) return null;
  return round1(c - p);
}

const TONES = new Set(['good', 'mid', 'warn']);
/** サーバーの status → 色。good / mid / warn だけ色が付く（none・未知は色なし）。 */
export const toneOf = (status) => (TONES.has(status) ? status : 'none');

/** 増減の表示。率は「▲ +20%」「▼ -4%」、ポイントは「▲ +3.2pt」。比べる先が無ければ null。 */
export function formatDelta(delta, kind = 'pct') {
  if (delta == null) return null;
  const unit = kind === 'pt' ? 'pt' : '%';
  if (delta === 0) return `± 0${unit}`;
  return `${delta > 0 ? '▲ +' : '▼ '}${delta}${unit}`;
}

/** data_as_of の表示（「〜10/14」）。日付が無ければ null。 */
export function asOfLabel(asOf) {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(asOf || '');
  return m ? `〜${Number(m[1])}/${Number(m[2])}` : null;
}

/** 表示する店舗: 指定が見える店舗にあればそれ、無ければ先頭（見える店舗が無ければ null）。 */
export function pickShop(stores, shop) {
  const list = stores || [];
  if (shop && list.some((s) => s.store === shop)) return shop;
  return list[0]?.store ?? null;
}

/** 「〜9/13」のような、先月同時期の最終日の表示。 */
function throughLabel(prev) {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(prev?.through || '');
  return m ? `〜${Number(m[1])}/${Number(m[2])}` : null;
}

/** 集計期間の表示（「10/1〜10/20」）。月初から data_as_of まで。日付が無ければ null。 */
export function periodLabel(asOf) {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(asOf || '');
  return m ? `${Number(m[1])}/1〜${Number(m[1])}/${Number(m[2])}` : null;
}

/**
 * ヒーロー（上段 = 目標を設定した人の売上、下段 = 参考の店舗全体）。
 * 大きい数字は「目標を設定した人の売上」、達成率 = それ ÷ 目標（月の途中も同じ）。白線 = 月の経過。
 * 色と「遅れ／先行」の判定は、サーバーの status（判定できない間は none = 色なし・遅れ／先行なし）。
 * @param entry   store-summary の 1 店舗分
 * @param summary store-summary の応答（elapsed_days / days_in_month / as_of を使う）
 * @param isAdmin 管理者ロールのときだけ、人数（目標を設定した n 名・未設定の n 名）を出す
 */
export function heroModel(entry, summary, { isAdmin = false } = {}) {
  if (!entry) return null;
  const sales = num(entry.mtd?.total_sales);
  const prevSales = num(entry.prev_month_same_period?.total_sales);
  const goalTotal = num(entry.goal_total) > 0 ? num(entry.goal_total) : null;
  const elapsed = num(summary?.elapsed_days) ?? 0;
  const days = num(summary?.days_in_month) ?? 0;
  const remaining = elapsed > 0 && days > 0 ? days - elapsed : null;
  const goalSet = num(entry.goal_set) ?? 0;
  const holderSales = num(entry.sales_of_goal_holders) ?? 0;
  const hasGoals = goalTotal != null && goalSet > 0;
  const salesPct = num(entry.sales_pct) == null ? null : Math.round(num(entry.sales_pct));
  const elapsedPct = elapsed > 0 && days > 0 ? Math.round((elapsed / days) * 100) : null;
  const judged = entry.status && entry.status !== 'none';
  const holderShare = sales > 0 ? Math.min(100, Math.round((holderSales / sales) * 100)) : null;
  return {
    store: entry.store,
    sales,
    prevSales,
    delta: pctChange(sales, prevSales),
    asOf: asOfLabel(summary?.as_of),
    period: periodLabel(summary?.as_of),
    prevThrough: throughLabel(entry.prev_month_same_period),
    hasGoals,
    target: goalTotal,
    salesPct,
    // 上段の大きい数字（目標を設定した人の売上）。目標が無いときは null
    holderSales: hasGoals && entry.sales_of_goal_holders != null ? holderSales : null,
    holderCount: isAdmin && goalSet > 0 ? goalSet : null,
    remainingAmount: hasGoals ? Math.max(0, goalTotal - holderSales) : null,
    status: entry.status,
    tone: toneOf(entry.status),
    elapsedPct,
    // ペースとの差（pt）。正 = 遅れ、負 = 先行。判定できない間（7 日未満・目標なし）は null
    paceGap: judged && salesPct != null && elapsedPct != null ? elapsedPct - salesPct : null,
    remainingDays: remaining,
    requiredPerDay:
      goalTotal != null && remaining > 0 ? Math.max(0, Math.ceil((goalTotal - holderSales) / remaining)) : null,
    // 下段（参考: 店舗全体）の内訳。目標のある人 / それ以外（目標未設定・アシスタント）
    otherSales: sales != null && hasGoals ? Math.max(0, sales - holderSales) : null,
    holderShare: hasGoals ? holderShare : null,
    otherShare: hasGoals && holderShare != null ? 100 - holderShare : null,
    unsetPeople: isAdmin && num(entry.people_n) > goalSet ? num(entry.people_n) - goalSet : 0,
  };
}

const BUCKET_LABELS = [
  ['good', 'できてる'],
  ['mid', 'もう少し'],
  ['warn', '要フォロー'],
  ['unset', '目標未設定'],
];

/**
 * 人数ブロック（管理者のみ）。判定できる日数に満たない間（judged = false）は、「目標未設定 N名」と
 * 文言だけを出す（できてる等は出さない）。
 */
export function bucketsModel(bucketsEntry, judged) {
  if (!bucketsEntry) return null;
  const b = bucketsEntry.buckets || {};
  const items = BUCKET_LABELS.map(([key, label]) => ({ key, label, n: num(b[key]) ?? 0 }));
  return {
    store: bucketsEntry.store,
    judged: Boolean(judged),
    items: judged ? items : items.filter((i) => i.key === 'unset'),
    note: judged ? null : '判定は 7 日分のデータがそろってから',
    total: num(bucketsEntry.people_n) ?? 0,
  };
}

/**
 * 全店比較の店舗カード 1 枚分。売上（〜昨日）・先月同時期との差・目標達成率（売上÷目標、色は status）・客単価。
 * 管理者だけ、人数チップ（できてる/もう少し/要フォロー/目標未設定。7 日分に満たない間は目標未設定だけ）。
 * myStore = ログイン本人の主店舗（「自店舗」の印）。個人名は含まない。
 */
export function cardModel(entry, { isAdmin = false, bucketsEntry = null, judged = true, myStore = null } = {}) {
  if (!entry) return null;
  const sales = num(entry.mtd?.total_sales);
  const pct = num(entry.sales_pct);
  return {
    store: entry.store,
    isMine: Boolean(myStore) && entry.store === myStore,
    sales,
    delta: pctChange(sales, entry.prev_month_same_period?.total_sales),
    salesPct: pct == null ? null : Math.round(pct),
    status: entry.status,
    tone: toneOf(entry.status),
    unitPrice: num(entry.mtd?.unit_price),
    chips: isAdmin && bucketsEntry ? bucketsModel(bucketsEntry, judged).items : null,
  };
}

/** 全店比較の並び: 達成率の低い順（手当てが要る順）。達成率が無い店舗は最後、同じなら店名順。 */
export function sortForCompare(stores) {
  const key = (s) => (num(s.sales_pct) == null ? Infinity : num(s.sales_pct));
  return [...(stores || [])].sort((a, b) => key(a) - key(b) || String(a.store).localeCompare(String(b.store), 'ja'));
}

/** 全店比較の上の 1 行（見えている店舗の合計）。達成率は、目標がある人の売上 ÷ その目標の和。 */
export function totalsModel(stores) {
  const list = stores || [];
  const sum = (pick) => list.reduce((a, s) => a + (num(pick(s)) ?? 0), 0);
  const sales = sum((s) => s.mtd?.total_sales);
  const prevSales = sum((s) => s.prev_month_same_period?.total_sales);
  const goal = sum((s) => s.goal_total);
  const holder = sum((s) => s.sales_of_goal_holders);
  return {
    count: list.length,
    sales,
    prevSales,
    delta: pctChange(sales, prevSales),
    customers: sum((s) => s.mtd?.total_customers),
    salesPct: goal > 0 ? Math.round((holder / goal) * 100) : null,
  };
}
